<script>
import { mapActions, mapState } from 'vuex';
import TriggerTemplateEdit from '../../components/Triggers/TriggerTemplateEdit';
import dateMixin from '../../mixins/dateMixin';

export default {
  name: 'TriggerTemplates',
  components: {
    TriggerTemplateEdit,
  },
  mixins: [
    dateMixin,
  ],
  data() {
    return {
      edit: false,
      template: undefined,
      type: 'create',
      headers: [
        { text: 'ID', value: 'id' },
        { text: 'Name', value: 'name' },
        { text: 'Subject', value: 'subject' },
        { text: 'Attachments', sortable: false },
        { text: 'Trigger Type', sortable: false },
        { text: 'Created', value: 'createdAt' },
        { text: 'Updated', value: 'updatedAt' },
        { text: 'Action', sortable: false, width: '40px', class: 'text-xs-center' },
      ],
      pagination: {
        rowsPerPage: 25,
        page: 1,
        sortBy: 'Created',
      },
      domainId: null,
    };
  },
  methods: {
    ...mapActions({
      setBreadcrumbs: 'nav/setBreadcrumbs',
      setDomain: 'triggers/setTemplateDomain',
      setTriggerType: 'triggers/setTemplateTriggerType',
      getDomains: 'triggers/loadDomains',
      getTriggerTypes: 'triggers/getTriggerTypes',
      getTemplates: 'triggers/getTemplates',
    }),
    domainChanged(domainId) {
      const foundDomain = this.domains.items.find(domain => domain.id === domainId);
      this.setDomain(foundDomain);
    },
    createTemplate() {
      this.$set(this, 'template', undefined);
      this.edit = !this.edit;
      this.type = 'create';
    },
    editTemplate(template) {
      this.template = template;
      this.edit = !this.edit;
      this.type = 'edit';
    },
    refreshTemplates() {
      this.getTemplates(this.domain.id);
    },
  },
  computed: {
    ...mapState({
      domains: state => state.triggers.domains,
      triggerTypes: state => state.triggers.types,
      templates: state => state.triggers.templates.templates,
      domain: state => state.triggers.templates.domain,
      triggerType: state => state.triggers.templates.triggerType,
    }),
    disableDomains() {
      return this.domains.loading ||
        this.domains.error ||
        this.domains.noContent ||
        this.templates.loading;
    },
    filteredTemplates() {
      return this.triggerType
        ? this.templates.items.filter(template => template.triggerType.id === this.triggerType.id)
        : [];
    },
  },
  created() {
    this.setBreadcrumbs({
      breadcrumbs: [
        { text: 'Triggers', to: '', disabled: true },
        { text: 'Templates', to: this.$route.path },
      ],
      title: 'Templates',
    });
    if (!this.domains.items.length && !this.domains.loading) {
      this.getDomains().then(() => {
        if (this.$route.query.domain) {
          const domainId = parseInt(this.$route.query.domain, 10);
          this.setDomain(this.domains.items.find(domain => domain.id === domainId));
          this.domainId = domainId;
        }
      });
    }
    this.getTriggerTypes();
  },
};
</script>
<template>
  <v-flex xs12>
    <v-card>
      <v-container grid-list>
        <v-layout row>
          <v-flex xs6>
            <v-autocomplete
              :value="domainId"
              :items="domains.items"
              :loading="domains.loading"
              :disabled="disableDomains"
              label="Choose Domain"
              item-value="id"
              item-text="name"
              dense
              @change="domainChanged"
            />
          </v-flex>
          <v-flex xs6>
            <v-autocomplete
              :value="triggerType"
              :items="triggerTypes.items"
              :loading="triggerTypes.loading"
              :disabled="triggerTypes.loading || triggerTypes.error"
              label="Choose Trigger Type"
              item-value="id"
              item-text="type"
              return-object
              @change="setTriggerType"
            />
          </v-flex>
          <v-flex xs6>
            <v-btn
              @click="createTemplate"
              :disabled="!domain || templates.loading || !triggerType"
              color="blue"
              class="white--text reminder-templates__create"
            >
              Create
            </v-btn>
          </v-flex>
        </v-layout>

        <v-layout row mt-4>
          <v-flex xs11 headline>
            Templates
          </v-flex>
          <v-flex xs1 text-xs-right>
            <v-btn
              @click="refreshTemplates"
              :disabled="templates.loading || domains.loading || !domain"
              :class="{ 'icon-spin': templates.loading }"
              icon
            >
              <v-icon>refresh</v-icon>
            </v-btn>
          </v-flex>
        </v-layout>
        <v-divider/>
        <v-layout row mt-4>
          <v-flex v-if="domain" xs12>
            <v-data-table
              :headers="headers"
              :pagination.sync="pagination"
              :items="filteredTemplates"
              :loading="templates.loading"
            >
              <template v-slot:items="props">
                <tr>
                  <td>{{ props.item.id }}</td>
                  <td>{{ props.item.name }}</td>
                  <td>{{ props.item.subject }}</td>
                  <td>
                    <template v-if="props.item.attachments.length">
                      {{ props.item.attachments.map(a => a.name).join(', ') }}
                    </template>
                    <span v-else class="caption text--grey">
                      -
                    </span>
                  </td>
                  <td>{{ props.item.triggerType.type }}</td>
                  <td>{{ formatDate(props.item.createdAt) }}</td>
                  <td>{{ formatDate(props.item.updatedAt) }}</td>
                  <td class="d-flex align-center">
                    <v-btn
                      @click="editTemplate(props.item)"
                      :disabled="templates.loading || domains.loading"
                      color="blue"
                      small
                    >
                      <v-icon color="white" small>edit</v-icon>
                    </v-btn>
                  </td>
                </tr>
              </template>
              <template v-slot:no-data>
                <v-flex xs12 text-xs-center>
                  <v-alert v-if="templates.error" :value="true" type="error">
                    Unable to load templates
                  </v-alert>
                  <span v-else-if="!templates.loading" class="caption grey--text font-italic">
                    No Templates found
                  </span>
                </v-flex>
              </template>
            </v-data-table>
          </v-flex>
          <v-flex v-else xs12 text-xs-center>
            <span class="caption grey--text">
              Select a domain to see templates
            </span>
          </v-flex>
        </v-layout>

        <trigger-template-edit
          :show="edit"
          :template="template"
          :type="type"
          :trigger-type="triggerType"
        />
      </v-container>
    </v-card>
  </v-flex>
</template>

<style lang="scss">
.reminder-templates {
  &__create {
    float: right;
    margin-top: 10px;
  }
}
</style>



// WEBPACK FOOTER //
// src/pages/triggers/TriggerTemplates.vue